import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { motion, useInView } from "framer-motion";
import PRICING from "../utils/PRICING.json";
import PlanSelectionModal from "../components/PlanSelectionModal";
import en from "../translations/en";
import fr from "../translations/fr";
import ar from "../translations/ar";

// Reusable fade-up animation wrapper - MOVED OUTSIDE to prevent re-creation
const FadeUp = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const PricingCard = ({ planKey, language, billingCycle, onSelectPlan, isSelected }) => {
  const isFr = language === "fr";
  const isAr = language === "ar";

  // Get localized data based on language
  const getPlanData = () => {
    const langKey = isAr ? "ar" : isFr ? "fr" : "en";
    return PRICING[langKey]?.plans?.[planKey];
  };

  const planData = getPlanData();
  if (!planData) return null;

  const name = planData.name;
  const description = planData.description;
  const price = planData.price;
  const features = planData.features || {};

  // Calculate price with 10% discount for yearly (same as Home.js logic)
  const displayPrice = billingCycle === "yearly" && price !== null 
    ? (price * 0.9).toFixed(2)
    : price !== null 
    ? price 
    : null;

  const currencyLabel = planData.currency;
  const billingLabel = billingCycle === "yearly" 
    ? (isAr ? "سنويًا" : isFr ? "Annuellement" : "Yearly")
    : (isAr ? "شهريًا" : isFr ? "Mensuellement" : "Monthly");

  const yearlyDiscount = billingCycle === "yearly" && price !== null
    ? (isAr ? "توفير 10%" : isFr ? "Économiser 10%" : "You save 10%")
    : null;

  // Get feature display - show ALL features
  const getFeatures = () => {
    const featureList = [];
    Object.entries(features).forEach(([key, value]) => {
      if (value.value) {
        featureList.push({
          name: value.name || key,
          enabled: value.value === true,
        });
      }
    });
    return featureList;
  };

  const featureList = getFeatures();

  const selectPlanText = isAr ? "اختر هذه الخطة" : isFr ? "Choisir ce plan" : "Select Plan";
  const isRecommended = planData.recommended;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`h-full border transition-all duration-300 rounded-xl p-6 sm:p-8 relative flex flex-col overflow-hidden ${
        isSelected
          ? "border-4 border-green-500 bg-green-50 shadow-2xl transform scale-110 ring-2 ring-green-500 ring-opacity-50"
          : isRecommended
          ? "border-2 border-teal-500 bg-gray-50 transform scale-105 shadow-lg"
          : "border-gray-200"
      } hover:border-teal-500 transition-colors`}
      dir={isAr ? "rtl" : "ltr"}
    >
      {isSelected && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg rounded-tr-lg shadow-lg z-20">
          {isAr ? "✓ مختار" : isFr ? "✓ SÉLECTIONNÉ" : "✓ SELECTED"}
        </div>
      )}
      {isRecommended && !isSelected && (
        <div className="absolute -top-2 -right-2 bg-teal-500 text-white text-xs font-bold px-3 py-1.5 rounded-bl-lg rounded-tr-lg shadow-lg z-20">
          {isAr ? "مُوصى به" : isFr ? "RECOMMANDÉ" : "RECOMMENDED"}
        </div>
      )}
      {/* Plan Header */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{name}</h3>
        <p className="text-gray-600 text-sm mb-6">{description}</p>

        {/* Pricing Display */}
        <div className="mb-6">
          {displayPrice !== null ? (
            <>
              <span className="text-4xl font-bold">{displayPrice}</span>
              <span className="text-gray-500"> {currencyLabel}</span>
              <div className="text-sm text-gray-500 mt-2">
                {billingLabel}
              </div>
              {yearlyDiscount && (
                <div className="text-xs text-green-600 font-semibold mt-1">
                  {yearlyDiscount}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-3xl font-bold text-teal-600 mb-2">
                {isAr ? "سعر مخصص" : isFr ? "Prix personnalisé" : "Custom Pricing"}
              </div>
              <p className="text-gray-600">
                {isAr ? "اتصل بنا للحصول على عرض" : isFr ? "Contactez-nous pour une offre" : "Contact us for details"}
              </p>
            </>
          )}
        </div>

        {/* Features List */}
        <div className="mb-8 flex-1">
          <h4 className="font-semibold text-gray-800 mb-4 text-sm">
            {isAr ? "الميزات:" : isFr ? "Fonctionnalités:" : "Features:"}
          </h4>
          <ul className="space-y-2">
            {featureList.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <svg
                  className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-700 text-sm">{feature.name}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Select Plan Button */}
        <motion.button
          onClick={() => onSelectPlan(planKey)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 mt-auto ${
            isSelected
              ? "bg-green-500 hover:bg-green-600 text-white"
              : isRecommended
              ? "bg-teal-500 hover:bg-teal-600 text-white shadow-lg"
              : "bg-gray-100 hover:bg-gray-200 text-gray-800"
          }`}
        >
          {isSelected
            ? (isAr ? "✓ مختار" : isFr ? "✓ Sélectionné" : "✓ Selected")
            : selectPlanText}
        </motion.button>
    </motion.div>
  );
};

function Pricing() {
  const language = useSelector((state) => state.language);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlanNotification, setSelectedPlanNotification] = useState(null);

  const isFr = language === "fr";
  const isAr = language === "ar";

  // Helper function to build complete plan notification data
  const buildPlanNotification = React.useCallback((planKey, cycle) => {
    const langKey = isAr ? "ar" : isFr ? "fr" : "en";
    const planData = PRICING[langKey]?.plans?.[planKey];
    
    if (!planData) return null;

    return {
      planKey,
      name: planData.name,
      nameAr: PRICING.ar?.plans?.[planKey]?.name,
      nameFr: PRICING.fr?.plans?.[planKey]?.name,
      nameEn: PRICING.en?.plans?.[planKey]?.name,
      description: planData.description,
      descAr: PRICING.ar?.plans?.[planKey]?.description,
      descFr: PRICING.fr?.plans?.[planKey]?.description,
      descEn: PRICING.en?.plans?.[planKey]?.description,
      price: planData.price,
      currency: planData.currency,
      billingCycle: cycle,
      recommended: planData.recommended,
    };
  }, [isAr, isFr]);

  // Load selected plan from localStorage on mount, default to "free"
  React.useEffect(() => {
    const savedPlan = localStorage.getItem("selectedPlan");
    if (savedPlan) {
      setSelectedPlanNotification(JSON.parse(savedPlan));
    } else {
      // Set default to "free" plan
      const defaultPlan = buildPlanNotification("free", "monthly");
      if (defaultPlan) {
        setSelectedPlanNotification(defaultPlan);
      }
    }
  }, [buildPlanNotification]);

  // Get translations
  const getTranslations = () => {
    switch (language) {
      case "fr":
        return fr;
      case "ar":
        return ar;
      default:
        return en;
    }
  };

  // Get localized data
  const getPricingData = () => {
    const langKey = isAr ? "ar" : isFr ? "fr" : "en";
    return PRICING[langKey]?.plans || {};
  };

  const t = getTranslations();
  const pricingPlans = getPricingData();
  const planKeys = Object.keys(pricingPlans);

  const handleSelectPlan = (planKey) => {
    // Always open modal when clicking any plan
    setSelectedPlan({ planKey, billingCycle });
    setIsModalOpen(true);
  };
  const titleText = t.pricing?.sectionTitle ||
    (isAr ? "خطط التسعير المرنة" : isFr ? "Plans de tarification flexibles" : "Flexible Pricing Plans");

  const subtitleText = t.pricing?.description ||
    (isAr ? "اختر الخطة التي تناسب احتياجات عيادتك" : isFr ? "Choisissez le plan qui correspond à vos besoins" : "Choose the plan that fits your clinic's needs");

  return (
    <div className={`min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 py-20 px-4`} dir={isAr ? "rtl" : "ltr"}>
      {/* Header Section */}
      <FadeUp className="text-center mb-8 md:mb-12 lg:mb-20">
        <div className="inline-block">
          <span className="text-sm font-semibold text-teal-600 bg-teal-50 px-4 py-2 rounded-full mb-4 inline-block">
            {isAr ? "💰 خطط ميسورة" : isFr ? "💰 Plans Abordables" : "💰 Affordable Plans"}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {titleText}
        </h1>
        <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-4">
          {subtitleText}
        </p>
        <p className="text-lg text-gray-500 max-w-2xl mx-auto">
          {isAr 
            ? "ابدأ مجانًا وارقِ إلى المزيد من الميزات عند الحاجة"
            : isFr
            ? "Commencez gratuitement et mettez à niveau pour plus de fonctionnalités"
            : "Start free and upgrade for more features as you grow"
          }
        </p>
      </FadeUp>

      {/* Billing Toggle */}
      <FadeUp delay={0.1} className="flex justify-center mb-16">
        <div className="inline-flex bg-gray-200 rounded-full p-1 shadow-md">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-8 py-3 rounded-full font-semibold transition-all ${
              billingCycle === "monthly"
                ? "bg-white text-teal-600 shadow-lg"
                : "text-gray-700 hover:text-gray-900"
            }`}
          >
            {language === "ar" ? "شهري" : language === "fr" ? "Mensuel" : "Monthly"}
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`px-8 py-3 rounded-full font-semibold transition-all relative ${
              billingCycle === "yearly"
                ? "bg-white text-teal-600 shadow-lg"
                : "text-gray-700 hover:text-gray-900"
            }`}
          >
            {language === "ar" ? "سنوي" : language === "fr" ? "Annuel" : "Yearly"}
            {billingCycle === "yearly" && (
              <span className="absolute -top-3 -right-3 bg-gradient-to-r from-green-400 to-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                {language === "ar" ? "توفير 10%" : language === "fr" ? "Économiser 10%" : "Save 10%"}
              </span>
            )}
          </button>
        </div>
      </FadeUp>

      {/* Selected Plan Notification - At top of pricing section */}
      {selectedPlanNotification && (
        <FadeUp delay={0.15} className="bg-gradient-to-r from-green-400 to-teal-500 text-white py-4 px-6 rounded-lg mb-12 shadow-lg max-w-4xl mx-auto">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-lg">
                {language === "ar" 
                  ? `✓ تم اختيار الخطة: ${selectedPlanNotification.nameAr || selectedPlanNotification.name}`
                  : language === "fr"
                  ? `✓ Plan sélectionné: ${selectedPlanNotification.nameFr || selectedPlanNotification.name}`
                  : `✓ Selected Plan: ${selectedPlanNotification.nameEn || selectedPlanNotification.name}`
                }
              </p>
              <p className="text-sm opacity-90 mt-1">
                {language === "ar" 
                  ? selectedPlanNotification.descAr || selectedPlanNotification.description
                  : language === "fr"
                  ? selectedPlanNotification.descFr || selectedPlanNotification.description
                  : selectedPlanNotification.descEn || selectedPlanNotification.description
                }
              </p>
              <p className="text-sm opacity-90 mt-2">
                {language === "ar" 
                  ? `السعر: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                  : language === "fr"
                  ? `Prix: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                  : `Price: ${selectedPlanNotification.billingCycle === "yearly" ? (selectedPlanNotification.price * 0.9).toFixed(2) : selectedPlanNotification.price} ${selectedPlanNotification.currency}`
                }
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // Open modal to change plan
                  setSelectedPlan({ planKey: selectedPlanNotification.planKey, billingCycle: selectedPlanNotification.billingCycle });
                  setIsModalOpen(true);
                }}
                className="bg-white hover:bg-gray-200 text-green-600 font-semibold px-4 py-2 rounded-lg transition whitespace-nowrap text-sm"
              >
                {language === "ar" ? "تغيير الخطة" : language === "fr" ? "Changer le plan" : "Change Plan"}
              </button>
              <button
                onClick={() => setSelectedPlanNotification(null)}
                className="text-white hover:text-gray-200 text-2xl transition"
              >
                ✕
              </button>
            </div>
          </div>
        </FadeUp>
      )}

      {/* Pricing Cards Grid */}
      <FadeUp delay={0.2} className=" mx-auto flex justify-center w-full">
        <div className={`grid gap-4 sm:gap-6 md:gap-8 w-full max-w-screen-2xl ${planKeys.length === 1 ? "grid-cols-1" : planKeys.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"}`}>
          {planKeys.map((planKey, index) => {
            // Hide free plan for yearly billing
            if (planKey === "free" && billingCycle === "yearly") {
              return null;
            }
            
            const isSelected = selectedPlanNotification && selectedPlanNotification.planKey === planKey;
            return (
              <div key={planKey} style={{ animationDelay: `${index * 0.1}s` }} className="h-full">
                <FadeUp delay={0.15 * (index + 1)} className="h-full">
                  <PricingCard
                    planKey={planKey}
                    language={language}
                    billingCycle={billingCycle}
                    onSelectPlan={handleSelectPlan}
                    isSelected={isSelected}
                  />
                </FadeUp>
              </div>
            );
          })}
        </div>
      </FadeUp>

      {/* FAQ Section */}
      <FadeUp delay={0.3} className="mt-32 max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {isAr ? "الأسئلة الشائعة" : isFr ? "Questions fréquemment posées" : "Frequently Asked Questions"}
          </h2>
          <p className="text-gray-600">
            {isAr ? "كل ما تحتاج إلى معرفته عن خططنا" : isFr ? "Tout ce que vous devez savoir sur nos plans" : "Everything you need to know about our plans"}
          </p>
        </div>

        <div className="space-y-4">
          {[
            {
              qEn: "Can I change my plan anytime?",
              qFr: "Puis-je changer mon plan à tout moment?",
              qAr: "هل يمكنني تغيير خطتي في أي وقت؟",
              aEn: "Yes, you can upgrade or downgrade your plan at any time with no penalties. Changes take effect at the next billing cycle.",
              aFr: "Oui, vous pouvez améliorer ou rétrograder votre plan à tout moment sans pénalité. Les modifications prendront effet au prochain cycle de facturation.",
              aAr: "نعم، يمكنك الترقية أو الخفض في أي وقت بدون عقوبات. تدخل التغييرات حيز التنفيذ في دورة الفواتير التالية.",
            },
            {
              qEn: "What payment methods do you accept?",
              qFr: "Quels modes de paiement acceptez-vous?",
              qAr: "ما هي طرق الدفع التي تقبلونها؟",
              aEn: "We accept credit cards (Visa, Mastercard, Amex), bank transfers, and mobile payments. All transactions are encrypted and secure.",
              aFr: "Nous acceptons les cartes de crédit (Visa, Mastercard, Amex), les virements bancaires et les paiements mobiles. Toutes les transactions sont chiffrées et sécurisées.",
              aAr: "نقبل بطاقات الائتمان (Visa, Mastercard, Amex) والتحويلات البنكية والدفع عبر الهاتف المحمول. جميع المعاملات مشفرة وآمنة.",
            },
            {
              qEn: "Is there a free trial available?",
              qFr: "Y a-t-il une période d'essai gratuite?",
              qAr: "هل هناك نسخة تجريبية مجانية متاحة؟",
              aEn: "Yes! Our Free plan lets you test all core features with 2 users and 10 patients. No credit card needed to start.",
              aFr: "Oui ! Notre plan gratuit vous permet de tester toutes les fonctionnalités principales avec 2 utilisateurs et 10 patients. Aucune carte de crédit requise.",
              aAr: "نعم! خطتنا المجانية تتيح لك اختبار جميع الميزات الأساسية مع مستخدمين و10 مرضى. لا حاجة لبطاقة ائتمان.",
            },
            {
              qEn: "Do you offer custom plans for enterprises?",
              qFr: "Proposez-vous des plans personnalisés pour les entreprises?",
              qAr: "هل تقدمون خطط مخصصة للمؤسسات؟",
              aEn: "Absolutely! Our Enterprise plan offers unlimited features, dedicated support, and custom integrations. Contact our sales team for details.",
              aFr: "Absolument ! Notre plan Entreprise offre des fonctionnalités illimitées, un support dédié et des intégrations personnalisées. Contactez notre équipe commerciale.",
              aAr: "بالتأكيد! خطتنا الخاصة بالمؤسسات توفر ميزات غير محدودة ودعم مخصص. اتصل بفريق المبيعات لدينا للحصول على التفاصيل.",
            },
            {
              qEn: "What about data security and backups?",
              qFr: "Qu'en est-il de la sécurité des données et des sauvegardes?",
              qAr: "ماذا عن أمان البيانات و المنسوخات الاحتياطية؟",
              aEn: "All plans include enterprise-grade security, automatic backups, and 99.9% uptime SLA. Your data is encrypted both in transit and at rest.",
              aFr: "Tous les plans incluent la sécurité de niveau entreprise, les sauvegardes automatiques et un SLA de disponibilité de 99,9 %. Vos données sont chiffrées en transit et au repos.",
              aAr: "تتضمن جميع الخطط أمانًا على مستوى المؤسسات والنسخ الاحتياطي التلقائي واتفاقية مستوى الخدمة بنسبة 99.9%. يتم تشفير بياناتك أثناء النقل والبقاء.",
            },
          ].map((faq, idx) => {
            const q = isAr ? faq.qAr : isFr ? faq.qFr : faq.qEn;
            const a = isAr ? faq.aAr : isFr ? faq.aFr : faq.aEn;
            return (
              <FadeUp key={idx} delay={0.05 * idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all border border-gray-100 hover:border-teal-200">
                <h3 className="font-bold text-gray-900 mb-3 flex items-start gap-3">
                  <span className="text-teal-600 font-black text-lg flex-shrink-0">Q</span>
                  {q}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed flex gap-3">
                  <span className="text-teal-600 font-black flex-shrink-0">A</span>
                  {a}
                </p>
              </FadeUp>
            );
          })}
        </div>
      </FadeUp>

      {/* CTA Section */}
      <FadeUp delay={0.4} className="mt-32 bg-gradient-to-r from-teal-500 via-blue-500 to-blue-600 text-white py-20 px-8 rounded-2xl max-w-4xl mx-auto text-center shadow-2xl overflow-hidden relative">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>
        <div className="relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">
            {isAr ? "هل أنت مستعد للبدء؟" : isFr ? "Prêt à commencer?" : "Ready to get started?"}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {isAr 
              ? "انضم إلى آلاف المتخصصين في الرعاية الصحية الذين يثقون بـ Raqib"
              : isFr
              ? "Rejoignez des milliers de professionnels de la santé qui font confiance à Raqib"
              : "Join thousands of healthcare professionals who trust Raqib"
            }
          </p>
          <motion.button 
            onClick={() => {
              setSelectedPlan({ planKey: "free", billingCycle: "monthly" });
              setIsModalOpen(true);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white hover:bg-green-50 text-teal-600 font-bold py-4 px-12 rounded-lg transition-all shadow-xl transform hover:shadow-2xl text-lg"
          >
            {isAr ? "ابدأ الآن مجانًا" : isFr ? "Commencer maintenant gratuitement" : "Start Free Today"}
          </motion.button>
        </div>
      </FadeUp>
      {selectedPlan && (
        <PlanSelectionModal
          plan={selectedPlan}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlan(null);
          }}
          billingCycle={billingCycle}
          onPlanSelected={() => {
            // Build complete plan notification data
            const planNotification = buildPlanNotification(selectedPlan.planKey, billingCycle);
            if (planNotification) {
              setSelectedPlanNotification(planNotification);
              localStorage.setItem("selectedPlan", JSON.stringify(planNotification));
            }
            setIsModalOpen(false);
            setSelectedPlan(null);
          }}
        />
      )}
    </div>
  );
}

export default Pricing;
